import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import axiosInstance, {
  clearAuthData,
  isJwtAuthFailure,
} from "../../components/content/axiosConfig";
import { enqueueSnackbar } from "notistack";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";

const UpdateStaff = () => {
  const history = useHistory();
  const { id } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [staffFisherId, setStaffFisherId] = useState(null);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    isAdmin: false,
    status: false,
  });

  useEffect(() => {
    const FetchStaffDetail = async () => {
      setIsLoading(true);
      try {
        const response = await axiosInstance.get("api/User/GetStaff", {
          params: { id: id },
        });
        const staff = response.data;

        setStaffFisherId(staff.FisherID ?? null);
        setFormData({
          fullName: staff.FullName || "",
          email: staff.Email || "",
          isAdmin: staff.IsAdmin || false,
          status: staff.Status || false,
        });
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        if (isJwtAuthFailure(error)) {
          clearAuthData();
          window.location.href = "/user/login";
          return;
        }
        console.error("Error fetching staff details:", error);

        const errorMessage = error.response?.data?.message || "An error occurred.";
        enqueueSnackbar(errorMessage, {
          variant: "error",
          anchorOrigin: {
            vertical: "top",
            horizontal: "right",
          },
          autoHideDuration: 3000,
        });
      }
    };

    FetchStaffDetail();
  }, [id]);

  const handleChange = (e) => {
    const { id: fieldId, value, type, checked, name } = e.target;
    if (type === "radio") {
      setFormData({
        ...formData,
        [name]: value === "true",
      });
    } else {
      setFormData({
        ...formData,
        [fieldId]: type === "checkbox" ? checked : value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axiosInstance.post(
        `api/User/UpdateStaff`,
        {
          FullName: formData.fullName,
          IsAdmin: formData.isAdmin,
          Status: formData.status,
          FisherID: staffFisherId,
        },
        {
          params: { id: id },
        }
      );
      showSuccessToast("Staff updated successfully");
      if (staffFisherId != null) {
        history.push(`/staff/${staffFisherId}`);
      } else {
        history.push("/accounts");
      }
    } catch (error) {
      setIsLoading(false);
      if (isJwtAuthFailure(error)) {
        clearAuthData();
        window.location.href = "/user/login";
        return;
      }
      const errorMessage = error.response?.data?.message || "An error occurred.";
      showErrorToast(errorMessage);
      console.error("Error updating staff:", error);
    }
  };

  const staffListPath = staffFisherId != null ? `/staff/${staffFisherId}` : "/accounts";

  return isLoading ? (
    <Preloader />
  ) : (
    <div>
      <form onSubmit={handleSubmit} className="fs-13px">
        <div className="d-flex align-items-center mb-3">
          <div>
            <ul className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to={staffListPath}>Staff</Link>
              </li>
            </ul>
            <h1 className="page-header mb-0">Update Staff</h1>
          </div>
          <div className="ms-auto">
            <button
              type="submit"
              className="btn btn-success btn-rounded px-4 rounded-pill"
            >
              <i className="fa fa-plus fa-lg me-2 ms-n2 text-success-900"></i>
              Submit
            </button>
          </div>
        </div>
        <div className="card border-0">
          <ul className="nav nav-tabs nav-tabs-v2 px-3">
            <li className="nav-item me-2">
              <Link className="nav-link px-2 active"> General </Link>
            </li>
            <li className="nav-item me-2">
              <Link to={"/changePassword/" + id} className="nav-link px-2">
                Password
              </Link>
            </li>
          </ul>
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              <div className="form-floating mb-15px">
                <input
                  type="text"
                  className="form-control h-45px fs-13px"
                  placeholder="Full Name"
                  id="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                />
                <label
                  htmlFor="fullName"
                  className="d-flex align-items-center fs-13px text-gray-600"
                >
                  Full Name
                </label>
              </div>

              <div className="form-floating mb-15px">
                <input
                  type="text"
                  className="form-control h-45px fs-13px"
                  placeholder="Email Address"
                  id="email"
                  value={formData.email}
                  onChange={handleChange}
                  readOnly={true}
                />
                <label
                  htmlFor="email"
                  className="d-flex align-items-center fs-13px text-gray-600"
                >
                  Email Address
                </label>
              </div>
              <div className="row">
                <div className="col-md-12 d-flex align-items-center">
                  <div className="col-md-12 d-flex align-items-center">
                    <div className="form-check mb-15px me-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="status"
                        checked={formData.status}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="status">
                        Status
                      </label>
                    </div>
                    <div className="form-check mb-15px me-3">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="isAdmin"
                        id="isAdmin"
                        value="true"
                        checked={formData.isAdmin === true}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="isAdmin">
                        Admin
                      </label>
                    </div>
                    <div className="form-check mb-15px">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="isAdmin"
                        id="isStaff"
                        value="false"
                        checked={formData.isAdmin === false}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="isStaff">
                        Staff
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UpdateStaff;
